<template>
    <v-menu v-model="isMenuOpen" :close-on-content-click="isChanged" offset-y min-width="290px" align-center>
        <template v-slot:activator="{ on }">
            <v-text-field
                v-model="date"
                :label="label"
                :rules="rules"
                :disabled="isDisabled"
                :error="error"
                :dense="dense"
                :class="textFieldClasses"
                day-format="YYYY/MM/DD"
                persistent-hint
                prepend-icon="event"
                class="text-body-2"
                :clearable="clearable"
                v-on="on"
                @focus="isTextFocused = true"
                @blur="isTextFocused = false"
                @update:error="e => $emit('update:error', e)"
            />
        </template>
        <v-date-picker
            ref="picker"
            v-model="validDate"
            :max="max"
            :min="min"
            :locale="locale"
            :day-format="dayFormat"
            :month-format="monthFormat"
            :disabled="isDisabled"
            no-title
        />
    </v-menu>
</template>

<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch, Emit } from 'vue-property-decorator';
import { getLocale } from '../../translations';
import { CurrentBrowserType } from '../../utils/types/BrowserInformationRelated';
import dayjs from 'dayjs';

@Component({
    name: 'CalenderTextField',
    components: {},
})
export default class CalenderTextField extends Vue {
    @Prop({ default: '' }) value: string;
    @Prop({ default: '' }) label: string;
    @Prop({ default: undefined }) max: string;
    @Prop({ default: undefined }) min: string;
    @Prop({ default: false }) disabled: boolean;
    @Prop({ default: false }) dense: boolean;
    @Prop({ default: false }) clearable: boolean;
    @Prop({ default: () => [] }) rules: [];
    @Prop({ default: undefined }) readonly error?: boolean;

    @Watch('value', { immediate: true })
    onValueChanged(val: string): void {
        this.date = val;
    }
    @Watch('date', { immediate: true })
    onDateChanged(val: string): void {
        this.input(val);
    }
    @Watch('isMenuOpen')
    onMenuOpenChanged(val: boolean): void {
        if (val) {
            this.isChanged = false;
        }
    }
    @Emit()
    public input(value: string) {}

    date = '';
    isTextFocused = false;
    isMenuOpen = false;
    isChanged = false;

    textFieldClasses = {
        'text-field-ie': CurrentBrowserType.isIEBrowser() || CurrentBrowserType.isTridentBrowser(),
    };

    /*
     * setter, getter
     */
    get locale(): string {
        return (this as any).$i18n?.locale() ?? getLocale().name;
    }
    get isDisabled(): boolean {
        return this.disabled;
    }
    get validDate(): string {
        const isValid = dayjs(this.date, 'YYYY-MM-DD', true).isValid();
        if (!isValid) {
            return '';
        }
        return this.date;
    }
    set validDate(value: string) {
        this.date = value;
        this.isChanged = true;
    }
    // IE11だけカレンダーを開くとテキストフィールドのフォーカスが外れてしまうので、
    // カレンダーを開いていたらフォーカスが当たっていることにする
    get isFocused(): boolean {
        if (this.isTextFocused) {
            return true;
        }
        if (this.isMenuOpen) {
            return true;
        }
        return false;
    }
    @Watch('isFocused')
    onFocusChanged(val: boolean) {
        if (val) {
            this.$emit('focus');
        } else {
            this.$emit('blur');
        }
    }

    /*
     * method
     */
    monthFormat(value: string): string {
        // new Date('YYYY-MM-DD') は引数をUTC時刻として扱い現地時刻に変換したオブジェクトを返す
        // 引数の月部分の言語対応版が必要なので、timeZone は UTC を指定する
        return this.locale == 'ja'
            ? new Date(value).toLocaleDateString(this.locale, { month: 'long', timeZone: 'UTC' })
            : new Date(value).toLocaleDateString(this.locale, { month: 'short', timeZone: 'UTC' });
    }
    dayFormat(value: string): string {
        // new Date('YYYY-MM-DD') は引数をUTC時刻として扱い現地時刻に変換したオブジェクトを返す
        // 引数の日部分が必要なので、 getUTCDate で取得する
        return new Date(value).getUTCDate().toString();
    }
}
</script>

<style lang="scss" scoped>
/* IE11でエラーメッセージをアニメーション表示すると表示がおかしくなることがあるので無効化 */
.text-field-ie :deep(.message-transition-enter-active) {
    transition: none !important;
}
.text-field-ie :deep(.message-transition-leave-active) {
    transition: none !important;
}
</style>
